import type { ReactNode } from 'react';
import { Flex } from '@chakra-ui/react';
import { Tags } from '@kula-ai/components';
import { jobStatusConfig } from '@/constants/job-constants';
import type { ClarifyOption, ClarifyOptionJob } from '../../../types';
import { StatusPill } from '../primitives';

// Minimal shape we hand the kula Select — structurally a SelectOption. Keeping
// it local avoids a deep type import while staying assignable to the prop.
export interface JobSelectOption {
  value: string;
  label: string;
  description?: string;
  rightElement?: ReactNode;
  data?: Record<string, unknown>;
}

// Fold the agent's status values onto the canonical job-status config keys so
// the tag colour + label match the jobs page (mirrors JobListBlock).
type StatusKey = keyof typeof jobStatusConfig;
const STATUS_ALIAS: Record<string, StatusKey> = {
  live: 'published',
  open: 'published',
  published: 'published',
  draft: 'draft',
  pending_approval: 'pending_approval',
  scheduled: 'scheduled',
  closed: 'closed',
  rejected: 'rejected',
  archived: 'archived',
};

const statusTag = (job: ClarifyOptionJob): ReactNode => {
  const key = job.status ? STATUS_ALIAS[job.status.toLowerCase()] : undefined;
  // "Open" is the default and adds no signal — job pickers are open jobs by
  // default — so only surface the exceptions (Unlisted, Draft, Closed…).
  if (key === 'published') return null;
  const cfg = key ? jobStatusConfig[key] : undefined;
  const label = cfg?.label ?? job.statusLabel;
  if (!label) return null;
  return <StatusPill label={label} color={cfg?.color} />;
};

// Second-line meta: department · office · work type, only the parts we have.
const metaLine = (job: ClarifyOptionJob): string =>
  [job.department, job.office, job.workType].filter(Boolean).join(' · ');

const KNOWN_STATUSES = new Set([
  'published',
  'live',
  'open',
  'draft',
  'pending_approval',
  'scheduled',
  'closed',
  'rejected',
  'archived',
  'unlisted',
]);

// Stopgap for when the backend hasn't attached structured `job` metadata yet:
// the flat label is a predictable "Title — Department · Location · status"
// string, so pull the real pieces out of it (CORE-4957). Trims the location to
// the office/city (first comma part) and drops the street address. Returns
// undefined when the label isn't in that shape (e.g. plain non-job options).
export const parseJobLabel = (label: string): ClarifyOptionJob | undefined => {
  const dash = label.indexOf(' — ');
  if (dash === -1) return undefined;
  const title = label.slice(0, dash).trim();
  const segs = label
    .slice(dash + 3)
    .split(' · ')
    .map((s) => s.trim())
    .filter(Boolean);
  const last = segs[segs.length - 1]?.toLowerCase().replace(/\s+/g, '_');
  const hasStatus = !!last && KNOWN_STATUSES.has(last);
  const meta = hasStatus ? segs.slice(0, -1) : segs;
  const office = meta[1]?.split(',')[0].trim();
  return {
    title,
    department: meta[0] || undefined,
    office: office || undefined,
    status: hasStatus ? segs[segs.length - 1] : undefined,
  };
};

// Turn a clarify option into a Select row. With structured `job` metadata (or
// the flat label parsed as a fallback) it renders a scannable title + meta line
// + status/Confidential tags; otherwise it degrades to the flat label
// (CORE-4957).
export const toJobSelectOption = (option: ClarifyOption): JobSelectOption => {
  const job = option.job ?? parseJobLabel(option.label);
  if (!job) return { value: option.value, label: option.label };

  const status = statusTag(job);
  const tags =
    status || job.confidential ? (
      <Flex align="center" gap="4px">
        {status}
        {job.confidential && <Tags label="Confidential" variant="neutral" />}
      </Flex>
    ) : undefined;

  return {
    value: option.value,
    label: job.title || option.label,
    description: metaLine(job) || undefined,
    rightElement: tags,
    data: { ...job },
  };
};
